<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { computed, ref } from 'vue';
import { useToast } from 'vue-toastification';
import { useAssessmentStore } from '@/stores/assessment';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    assessment: {
        type: Object,
        default: () => ({})
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal']);
const toast = useToast();

const assessmentStore = useAssessmentStore();
const { storeAssessment, updateAssessment } = assessmentStore;

const optionsStore = useOptionsStore();
const {
    assessmentDomainOptions,
    assessmentTypeOptions,
    questionTypeOptions,
    subDomainOptions,
} = storeToRefs(optionsStore);

const firstQuestionType = computed(() => questionTypeOptions.value?.[0]?.value ?? null);
const firstAssessmentType = computed(() => assessmentTypeOptions.value?.[0]?.value ?? null);

const normalizeQuestions = () => {
    if (!props.assessment?.questions?.length) {
        return [{
            hash: null,
            domain_id: props.assessment?.domain_id ?? props.assessment?.domain?.id ?? null,
            sub_domain_id: null,
            question_text: '',
            type_id: firstQuestionType.value,
            order: 0,
        }];
    }

    return props.assessment.questions.map((question, index) => ({
        hash: question.hash ?? null,
        domain_id: question.domain_id ?? question.domain?.id ?? props.assessment?.domain_id ?? null,
        sub_domain_id: question.sub_domain_id ?? question.sub_domain?.id ?? null,
        question_text: question.question_text ?? '',
        type_id: question.type_id ?? question.type?.id ?? null,
        order: question.order ?? index,
    }));
};

const form = ref({
    name: props.assessment?.name ?? '',
    version: props.assessment?.version ?? '1.0',
    type_id: props.assessment?.type_id ?? props.assessment?.type?.id ?? firstAssessmentType.value,
    domain_id: props.assessment?.domain_id ?? props.assessment?.domain?.id ?? null,
    description: props.assessment?.description ?? '',
    is_active: props.assessment?.is_active ?? true,
    questions: normalizeQuestions(),
});

const filteredSubDomainOptions = (domainId) => {
    return subDomainOptions.value.filter((option) => option.domain_id === domainId);
};

const addQuestion = () => {
    form.value.questions.push({
        hash: null,
        domain_id: form.value.domain_id,
        sub_domain_id: null,
        question_text: '',
        type_id: firstQuestionType.value,
        order: form.value.questions.length,
    });
};

const removeQuestion = (index) => {
    if (form.value.questions.length === 1) {
        toast.error(trans('label.at_least_one_question_required'));
        return;
    }

    form.value.questions.splice(index, 1);
};

const clearQuestionSubDomain = (question) => {
    question.sub_domain_id = null;
};

const payload = () => ({
    ...form.value,
    hash: props.assessment?.hash || null,
    questions: form.value.questions.map((question, index) => ({
        ...question,
        order: index,
    })),
});

const save = async () => {
    const action = props.mode === 'Edit' ? updateAssessment : storeAssessment;

    try {
        await action(payload());
        toast.success(trans(
            props.mode === 'Edit' ? 'actions.update.success' : 'actions.create.success',
            { type: trans('label.assessment') }
        ));
        emit('close-modal');
    } catch (error) {
        toast.error(error?.response?.data?.message || trans('actions.unexpected_error'));
    }
};
</script>

<template>
<ModalWrapper>
    <template #header>{{ $t('label.mode_what', { mode, what: $t('label.assessment') }) }}</template>
    <template #subheader>{{ $t('label.configure_assessment_content') }}</template>

    <form class="space-y-6" @submit.prevent="save">
        <div class="grid md:grid-cols-2 gap-x-4 gap-y-3">
            <StyledInput
                v-model="form.name"
                name="name"
                :label="$t('label.assessment_name')"
                :clearable="false" />

            <StyledInput
                v-model="form.version"
                name="version"
                :label="$t('label.version')"
                :clearable="false" />

            <StyledDropdown
                v-model="form.type_id"
                name="type_id"
                :options="assessmentTypeOptions"
                :label="$t('label.assessment_type')"
                :clearable="false" />

            <StyledDropdown
                v-model="form.domain_id"
                name="domain_id"
                :options="assessmentDomainOptions"
                :label="$t('label.primary_category')"
                :clearable="false" />

            <StyledRoundSwitch
                v-model="form.is_active"
                name="is_active"
                :label="form.is_active ? $t('label.active') : $t('label.inactive')"
                inset />

            <div class="md:col-span-2">
                <StyledTextArea
                    v-model="form.description"
                    name="description"
                    :label="$t('label.description')" />
            </div>
        </div>

        <div class="border-t pt-5 space-y-4">
            <div class="flex items-center justify-between gap-4">
                <h3 class="font-semibold text-lg text-gray-900">
                    {{ $t('label.questions') }}
                </h3>
                <StyledButton type="button" size="small" @click="addQuestion">
                    <FontAwesomeIcon icon="plus" class="mr-1" />
                    {{ $t('label.add_question') }}
                </StyledButton>
            </div>

            <div
                v-for="(question, index) in form.questions"
                :key="question.hash || index"
                class="rounded-lg border border-gray-200 p-4 space-y-3">
                <div class="flex justify-between items-center gap-4">
                    <p class="font-semibold text-gray-700">
                        {{ $t('label.question') }} {{ index + 1 }}
                    </p>
                    <StyledButton
                        type="button"
                        size="small"
                        variant="outlined"
                        @click="removeQuestion(index)">
                        {{ $t('label.remove') }}
                    </StyledButton>
                </div>

                <div class="grid md:grid-cols-3 gap-x-4 gap-y-3">
                    <StyledDropdown
                        v-model="question.domain_id"
                        :name="`questions.${index}.domain_id`"
                        :options="assessmentDomainOptions"
                        :label="$t('label.core_category')"
                        :clearable="false"
                        @input="clearQuestionSubDomain(question)" />

                    <StyledDropdown
                        v-model="question.sub_domain_id"
                        :name="`questions.${index}.sub_domain_id`"
                        :options="filteredSubDomainOptions(question.domain_id)"
                        :label="$t('label.subcategory')"
                        clearable />

                    <StyledDropdown
                        v-model="question.type_id"
                        :name="`questions.${index}.type_id`"
                        :options="questionTypeOptions"
                        :label="$t('label.item_type')"
                        :clearable="false" />
                </div>

                <StyledTextArea
                    v-model="question.question_text"
                    :name="`questions.${index}.question_text`"
                    :label="$t('label.question_text')" />
            </div>
        </div>
    </form>

    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
